import { useState, type FC } from 'react';

import { GenericButton } from '@shared/components/ui/custom/buttons/GenericButton';
import { cn } from '@shared/utils/tailwind.utils';

import { type AgentType } from '../../create-agent.utils';
import { AGENT_TYPE } from '../../create-agent.utils';

interface SelectAgentTypeProps {
  onAgentTypeSelected: (selectedAgentType: AgentType) => void;
  onClose: () => void;
}

export const SelectAgentType: FC<SelectAgentTypeProps> = ({ onAgentTypeSelected, onClose }) => {
  const [localSelection, setLocalSelection] = useState<AgentType>(AGENT_TYPE.STANDALONE_AGENT);
  const buttonBaseClass =
    'flex flex-col items-start p-5 border text-left transition-all w-full cursor-pointer rounded-xl active:scale-[0.99]';

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={() => setLocalSelection(AGENT_TYPE.STANDALONE_AGENT)}
        aria-pressed={localSelection === AGENT_TYPE.STANDALONE_AGENT}
        className={cn(
          buttonBaseClass,
          'transition-all duration-200 ease-out',
          localSelection === AGENT_TYPE.STANDALONE_AGENT
            ? 'border-cyan-500/80 bg-cyan-950/10 shadow-[0_0_12px_rgba(6,182,212,0.15)]'
            : 'border-border hover:border-cyan-500/30 hover:bg-cyan-500/[0.02]',
        )}
      >
        <div className="flex items-center gap-2">
          <span className="font-bold text-sm tracking-tight text-foreground">Standalone Agent</span>
        </div>
        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
          Deploy a lightweight, standalone agent directly on your infrastructure. No prior Onapsis products required.
        </p>
      </button>

      <button
        type="button"
        onClick={() => setLocalSelection(AGENT_TYPE.OP_AGENT)}
        aria-pressed={localSelection === AGENT_TYPE.OP_AGENT}
        className={cn(
          buttonBaseClass,
          'transition-all duration-200 ease-out',
          localSelection === AGENT_TYPE.OP_AGENT
            ? 'border-cyan-500/80 bg-cyan-950/10 shadow-[0_0_12px_rgba(6,182,212,0.15)]'
            : 'border-border hover:border-cyan-500/30 hover:bg-cyan-500/[0.02]',
        )}
      >
        <span className="font-bold text-sm tracking-tight text-foreground">Onapsis Platform (OP) Integration</span>
        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
          Leverage an agent already deployed and configured with your existing Onapsis Platform. Other Onapsis products
          required.
        </p>
      </button>

      <div className="p-4 bg-muted/40 border border-transparent rounded-xl text-xs text-muted-foreground leading-relaxed mt-2">
        <span className="font-bold">Note:</span> A tenant must choose one deployment strategy. If you use the Onapsis
        Platform (OP), you must utilize the OP integration for all connections.
      </div>

      <div className="flex justify-end pt-2 gap-3">
        <GenericButton variant="secondary" onClick={onClose} type="button" label="Cancel" />
        <GenericButton
          variant="default"
          type="button"
          label="Select"
          disabled={!localSelection}
          onClick={() => onAgentTypeSelected(localSelection)}
        />
      </div>
    </div>
  );
};
